import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { hasActiveAccess } from "@/lib/auth/tier";
import { useAuth } from "@/hooks/useAuth";

// Redirects unauthenticated users to /login, preserving the original
// destination so the login flow can return them after sign-in.
export function RequireAuth({
  children,
  flagship = false,
}: {
  children: ReactNode;
  flagship?: boolean;
}) {
  const { status, user } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return (
      <main className="mx-auto max-w-2xl px-6 py-24 text-sm text-neutral-500">
        Loading…
      </main>
    );
  }

  if (status !== "authenticated") {
    if (typeof window !== "undefined") {
      const postLogoutRedirect = window.sessionStorage.getItem(
        "abg-post-logout-redirect",
      );
      if (postLogoutRedirect) {
        window.sessionStorage.removeItem("abg-post-logout-redirect");
        return <Navigate to={postLogoutRedirect} replace />;
      }
    }
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (
    flagship &&
    !user?.admin &&
    !hasActiveAccess({ tier: user?.tier, expiresAt: user?.expires_at })
  ) {
    return <Navigate to="/purchase?plan=flagship" replace />;
  }

  return <>{children}</>;
}
